import React from 'react';
import { Grid, GridItem, Heading, Alert, TextInput, Radio, RadioGroup, Button, useHonestFormContext, Text, } from '@digitools/honest-ui';
import { empIdValidation, ssnValidation } from 'src/packages/eoi/utils/FormFieldValidations';
import useExperience from 'src/hooks/useExperience';
import { Experience } from 'src/types/Experience';
import useTranslate from 'src/hooks/useTranslate';
import { VerificationText } from '../../constants/VerificationText';
import { PersonalInformationText } from 'src/packages/as/constants/PersonalInformationText';
import useAsCustomerConfigContext from '../../hooks/useAsConfigContext';
import useAsDataContext from '../../hooks/useAsDataContext';
import { CustomEmployeeIdText, useCustomEmployeeText } from 'src/utils/CustomExperienceText';
import styled from 'styled-components';
const containerStyle = {
    border: '1px solid var(--color-gray-300)',
    borderRadius: 'var(--radius-md)',
    padding: 'var(--spacing-xl)',
    paddingBottom: 'var(--spacing-md)',
};
const fullWidth = { colSpan: 4, colSpanMd: 6, colSpanLg: 12 };
const fieldWidth = { colSpan: 4, colSpanMd: 6, colSpanLg: 3 };
const buttonWidth = { colSpan: 4, colSpanMd: 6, colSpanLg: 2 };
const StyledTextInput = styled(TextInput) `
  .hui-input__field-wrapper {
    border: 2px solid var(--color-gray-300);
  }
`;
const PersonalIdentityVerificationForm = ({ invalidUserEmail }) => {
    const { experience } = useExperience();
    const { watch } = useHonestFormContext();
    let radioSelected = 'ssn';
    const { t } = useTranslate();
    const customEmployeeText = useCustomEmployeeText();
    const employeeIdLabel = CustomEmployeeIdText(t(VerificationText.employeeIdLabel));
    const validateSsn = (ssn) => (ssnValidation(ssn) ? t(VerificationText.invalidSsn) : undefined);
    const validateEmpId = (empId) => (empIdValidation(empId) ? t(VerificationText.invalidEmpId) : undefined);
    const asConfigContext = useAsCustomerConfigContext();
    let lookupByEmpId = false;
    let lookupBySsn = false;
    const { claimantData } = useAsDataContext();
    if (asConfigContext.asConfig?.config?.uniqueIdIndicator === 'EMP ID') {
        lookupByEmpId = true;
    }
    else if (asConfigContext.asConfig?.config?.uniqueIdIndicator === 'SSN') {
        lookupBySsn = true;
    }
    else if (asConfigContext.asConfig?.config?.uniqueIdIndicator === 'Both') {
        lookupByEmpId = true;
        lookupBySsn = true;
    }
    const lastName = asConfigContext?.asConfig?.config?.userData?.lastName;
    const employeeId = asConfigContext && asConfigContext?.asConfig?.config?.userData?.employeeId;
    const initialEmpIDValue = () => {
        if (!asConfigContext.asConfig?.config?.prePopulateInterviewIndicator) {
            if (asConfigContext.asConfig?.config?.userData?.employeeId) {
                return asConfigContext.asConfig?.config?.userData?.employeeId;
            }
            return claimantData?.empId;
        }
    };
    const empIdValue = experience === Experience.EMPLOYEE ? initialEmpIDValue() : employeeId;
    const verificationHeadingLabel = customEmployeeText(experience === Experience.EMPLOYEE
        ? t(VerificationText.verificationHeadingEE)
        : `${t(VerificationText.verificationHeadingER)}`);
    const verificationSubHeading = experience === Experience.EMPLOYER && t(VerificationText.verificationSubHeading);
    if (claimantData?.empId)
        radioSelected = 'empID';
    const hasRadioGroup = lookupBySsn && lookupByEmpId;
    const verificationType = hasRadioGroup
        ? watch('accommodationData.verificationType') ?? radioSelected
        : undefined;
    const showSsn = (lookupBySsn && !lookupByEmpId) || verificationType === 'ssn';
    const showEmpId = (!lookupBySsn && lookupByEmpId) || verificationType === 'empID';
    return (<div style={containerStyle}>
      <Heading as={'h3'} weight='bold' color='secondary' className='hui-mb-md' data-public={true}>
        {verificationHeadingLabel}
      </Heading>
      {hasRadioGroup && (<div data-public={true} data-testid='subHeading'>
          <Text weight='bold' size='md' className='hui-mb-xs'>{verificationSubHeading}</Text>
        </div>)}
      <div data-testid={'error-alert-useremail'}>
        <Alert type={'error'} className='hui-mb-md' isOpen={!!invalidUserEmail}>{t(VerificationText.invalidUserEmail)}</Alert>
      </div>
      <Grid noGutter style={{ rowGap: '0.5rem' }}>
        {hasRadioGroup && (<GridItem {...fullWidth} data-testid='verification-radio-group' data-public={true}>
            <RadioGroup label={customEmployeeText(t(VerificationText.verifyRadioButtonLegend))} hideLabel={true} name={'accommodationData.verificationType'} required={true} defaultValue={radioSelected}>
              <Radio className='hui-mb-xs' id='showSsn' label={t(VerificationText.ssnRadioLabel)} value={'ssn'}/>
              <Radio className='hui-mb-xs mt-0' id='showEmpID' label={employeeIdLabel} value={'empID'}/>
            </RadioGroup>
          </GridItem>)}
        {showSsn && (<GridItem {...fieldWidth}>
            <StyledTextInput data-testid='verification-ssn-field' name={'accommodationData.ssn'} label={t(VerificationText.ssnLabel)} placeholder={t(VerificationText.ssnLabel)} hideLabel validate={validateSsn} autoComplete='off' required={true} data-private={true} masked={true} defaultValue={claimantData?.ssn}/>
          </GridItem>)}
        {showEmpId && (<GridItem {...fieldWidth}>
            <StyledTextInput data-testid='verification-empID-field' name={'accommodationData.empID'} label={employeeIdLabel} placeholder={employeeIdLabel} hideLabel type={'text'} validate={validateEmpId} required={true} defaultValue={experience === Experience.EMPLOYEE ? claimantData?.empId : empIdValue} data-private={true}/>
          </GridItem>)}
        {experience === Experience.EMPLOYER && asConfigContext?.asConfig && (<GridItem {...fieldWidth}>
            <StyledTextInput data-testid='verification-last-name-field' data-private={true} name={'accommodationData.lastName'} label={t(PersonalInformationText.lastNameQuestionLabel)} placeholder={t(PersonalInformationText.lastNameQuestionLabel)} hideLabel required={true} defaultValue={lastName}/>
          </GridItem>)}
        <GridItem {...buttonWidth} data-public={true}>
          <Button style={{ minWidth: '6.5rem' }} type={'primary'} label={t(VerificationText.verifyButton)} htmlType='submit'/>
        </GridItem>
      </Grid>
    </div>);
};
export default PersonalIdentityVerificationForm;
